import React, { useState, useEffect, useCallback } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useConfirmDialog } from '../hooks/useConfirmDialog';
//import { useUnsavedChangesPrompt } from '../hooks/useUnsavedChangesPrompt';
import PropertiesPanel from '../components/PropertiesPanel';
import PagesPanel from '../components/PagesPanel';
import RelationshipsPanel from '../components/RelationshipsPanel';
import AutoFillPanel from '../components/AutoFillPanel';
//import AutomationPanel from '../components/AutomationPanel';
import '../styles/TableEditor.css';
import { AutomationPanel } from '../components/AutomationPanel';
import API from '../api/axiosInstance';
import { useTheme } from '@mui/material/styles';
import { Box, Button, ButtonGroup, Container, Typography } from '@mui/material';
import RulesPanel from '../components/RulesPanel';


const TableEditor: React.FC = () => {
    const navigate = useNavigate();
    const [selectedTab, setSelectedTab] = useState('Properties');
    const [table, setTable] = useState<any | null>(null);
    const [isDirty, setIsDirty] = useState(false);
    const { showConfirmDialog, ConfirmDialogComponent } = useConfirmDialog();
    const [saveStatus, setSaveStatus] = useState<'idle' | 'saving' | 'success' | 'error'>('idle');
    const [saveMessage, setSaveMessage] = useState('');
    const [showSaveDialog, setShowSaveDialog] = useState(false);

    // Read URL params
    const params = useParams();
    const location = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const tableId = params.tableId ?? queryParams.get("tableId") ?? "";

    const confirmNavigation = useCallback(() => {
        return new Promise<boolean>((resolve) => {
            showConfirmDialog({
                title: 'Unsaved Changes',
                message: 'You have unsaved changes. Are you sure you want to leave?',
                onConfirm: () => resolve(true),
                onCancel: () => resolve(false),
            });
        });
    }, [showConfirmDialog]);

    const theme = useTheme();

    //useUnsavedChangesPrompt(isDirty);

    //useEffect(() => {
    //    if (!tableId) {
    //        navigate('/tables');
    //    }
    //}, [tableId, navigate]);

    useEffect(() => {
        const fetchTableData = async () => {
            try {
                console.log("tableId from params:", tableId);

                const user = JSON.parse(localStorage.getItem('user') || '{}');
                if (!user) return navigate("/login");
                const headers = {
                    headers: {
                        authToken: user.user.authtoken,
                        sessionId: user.user.sessionId
                    }
                };

                const tableRes = await API.get(`/tables/${tableId}`, headers);
                const table = tableRes.data;

                if (!table || !table.tableID) {
                    console.warn("Table not found or invalid response", tableRes.data);
                    //navigate('/tables');
                    return;
                }

                // Default empty arrays if missing
                table.description = table.description != null ? table.description : "";
                table.fields = Array.isArray(table.fields) ? table.fields : [];
                table.pages = Array.isArray(table.pages) ? table.pages : [];
                table.relationships = Array.isArray(table.relationships) ? table.relationships : [];


                setTable(table);

                setIsDirty(false);
            } catch (err: any) {
                console.error("Error fetching table", err);

                if (err?.response?.status === 401 || err?.response?.status === 403) {
                    // Unauthorized or session expired
                    navigate('/login');
                } else {
                    // Other failure like 404, etc.
                    //navigate('/tables');
                }
            }
        };

        if (tableId) {
            fetchTableData();
        } else {
            navigate('/tables');
        }
    }, [tableId, navigate]);

    const acquireStructureLock = async (tableId: string) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            await API.post(`/locks/TABLE/${tableId}?lockType=STRUCTURE`, {}, headers);
            return true;
        } catch (err: any) {
            console.error("Lock failed:", err);
            setSaveMessage(err?.response?.data?.message || "Failed to acquire lock.");
            setSaveStatus('error');
            setShowSaveDialog(true);
            return false;
        }
    };

    const releaseStructureLock = async (tableId: string) => {
        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            await API.delete(`/locks/TABLE/${tableId}`, headers);
        } catch (err) {
            console.warn("Failed to release lock (non-blocking):", err);
            // Optional: you can show a warning here if needed
        }
    };


    const handleSave = async () => {
        if (!table || !table.tableName?.trim()) {
            alert('Please enter a table name.');
            return;
        }
        if (!table.fields || !Array.isArray(table.fields) || table.fields.length === 0) {
            alert('Please add at least one field to the table.');
            return;
        }

        setShowSaveDialog(true);
        setSaveStatus('saving');
        setSaveMessage('');

        const lockAcquired = await acquireStructureLock(table.tableID);
        if (!lockAcquired){
            setSaveStatus('error');
            setSaveMessage('Unable to lock table for update.');
            return;
        } 

        // Make sure the fields are sorted and numbered before sending
        const updatedFields = [...table.fields].sort((a, b) => a.displayOrder - b.displayOrder)
            .map((field, index) => ({
                ...field,
                displayOrder: index
            }));

        const updatedTable = {
            ...table,
            fields: updatedFields
        };

        try {
            const user = JSON.parse(localStorage.getItem('user') || '{}');
            const headers = {
                headers: {
                    authToken: user.user?.authtoken,
                    sessionId: user.user?.sessionId,
                },
            };

            await API.put(`/tables/${table.tableID}`, updatedTable, headers);

            setIsDirty(false);
            setSaveStatus('success');

            setTimeout(() => {
                setShowSaveDialog(false);
                setSaveStatus('idle');
            }, 1500);
        } catch (err: any) {
            console.error("Save failed", err);
            setSaveStatus('error');

            const errorMsg = err?.response?.data;
            if (typeof errorMsg === "string") {
                setSaveMessage(errorMsg);
            } else {
                setSaveMessage(JSON.stringify(errorMsg));
            }
        } finally {
            await releaseStructureLock(table.tableID);
        }
    };




    const handleTabSwitch = (tab: string) => {
        setSelectedTab(tab);
    };

    //const handleFieldChange = (updatedFields: any[]) => {
    //    setFields(updatedFields);
    //    setIsDirty(true);
    //};

    //const handleTableNameChange = (name: string) => {
    //    setTableName(name);
    //    setIsDirty(true);
    //};

    const [validationError, setValidationError] = useState(false);

    const handleValidationError = (hasError: boolean) => {
        setValidationError(hasError);
    };

    return (
        <Box
            className="table-editor"
            sx={{
                padding: 2,
                backgroundColor: theme.palette.background.default,
                color: theme.palette.text.primary,
                minHeight: '100vh',
            }}
        > <Container
                maxWidth="xl"
                sx={{
                    mt: 5, // Push down from the top more than the default mt: 4

                }}
            />
            {ConfirmDialogComponent}
            <header className="header-menu">
                <h2>Table Editor: {table?.tableName ?? 'Untitled'}</h2>
            </header>

            {/* Tab Navigation */}
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSave}
                >
                    Save
                </Button>

                <ButtonGroup variant="outlined" color="primary">
                    {['Properties', 'Pages', 'Relationships', 'Auto-Fills', 'Flags', 'Automation'].map((tab) => (
                        <Button
                            key={tab}
                            variant={selectedTab === tab ? "contained" : "outlined"}
                            onClick={() => handleTabSwitch(tab)}
                            sx={{
                                textTransform: 'none',
                                fontWeight: selectedTab === tab ? 'bold' : 'normal',
                            }}
                        >
                            {tab}
                        </Button>
                    ))}
                </ButtonGroup>
            </Box>
            {table && !table.published && (
                <Typography variant="body2" color="warning.main" sx={{ mb: 2 }}>
                    ⚠️ This table is currently unpublished.
                </Typography>
            )}


            {/* Content Panels */}
            <div className="content-panel">
                {selectedTab === "Properties" && table && (
                    <PropertiesPanel
                        table={table}
                        setTable={setTable}
                        setIsDirty={setIsDirty}
                        onValidationError={handleValidationError}
                    />
                )}

                {selectedTab === "Pages" && table && (
                    <PagesPanel table={table} setTable={setTable} setIsDirty={setIsDirty} />
                )}

                {selectedTab === "Relationships" && table && (
                    <RelationshipsPanel table={table} setTable={setTable} setIsDirty={setIsDirty} />
                )}

                {selectedTab === "Auto-Fills" && <AutoFillPanel tableId={tableId} />}
                {selectedTab === "Flags" && table && (
                    <RulesPanel table={table} tenantId={table.tenantID} />
                )}
                {selectedTab === "Automation" && <AutomationPanel tableId={tableId} />}
            </div>

            {showSaveDialog && (
                <Box
                    sx={{
                        position: 'fixed',
                        top: 100,
                        left: '50%',
                        transform: 'translateX(-50%)',
                        backgroundColor: 'background.paper',
                        border: '1px solid',
                        borderColor: saveStatus === 'error' ? 'error.main' : 'success.main',
                        color: saveStatus === 'error' ? 'error.main' : 'success.main',
                        p: 2,
                        borderRadius: 2,
                        boxShadow: 3,
                        zIndex: 1300,
                        maxWidth: '80vw',
                        maxHeight: '40vh',
                        overflowY: 'auto',
                    }}
                    onClick={() => setShowSaveDialog(false)} // close on click
                >
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                        <Typography fontWeight="bold">
                            {saveStatus === 'saving' && 'Saving...'}
                            {saveStatus === 'success' && 'Save successful!'}
                            {saveStatus === 'error' && 'Failed to save'}
                        </Typography>
                        <Button size="small" onClick={() => setShowSaveDialog(false)}>
                            Close
                        </Button>
                    </Box>
                    {saveStatus === 'error' && (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
                            {typeof saveMessage === 'string'
                                ? (saveMessage.length > 500 ? saveMessage.substring(0, 500) + '…' : saveMessage)
                                : JSON.stringify(saveMessage, null, 2)}
                        </Typography>
                    )}
                </Box>
            )}


        </Box>
    );
};

export default TableEditor;
